import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import {
  InvestmentColumnKeys,
  investmentPrettifyText,
  investmentUploadColumnValidations,
  investmentUploadColumns,
  isInvestmentColumnDate,
} from '../utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { getColumnMapping } from '../ContributionMapping/worker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import { copyToClipboard } from 'shared/utils/clipboard';
import CopyButton from './CopyButton';
import { thinScrollbarStyles } from 'shared/design-system/settings/variables/Scrollbar';

export type ContributionModalOpen = {
  rowIndex: number;
  columnIndex: number;
  value: string;
  systemKey: InvestmentColumnKeys;
};

interface CellUpdateModalContentProps {
  value: ContributionModalOpen;
  row: string[];
  columns: ReturnType<typeof getColumnMapping>;
  handleClose: () => unknown;
  handleUpdateRow: (
    rowIndex: number,
    columnIndex: number,
    systemKey: InvestmentColumnKeys,
    value: string,
  ) => unknown;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.small} ${SPACERS.medium} ${SPACERS.large} ${SPACERS.medium};
  .textfield-tag {
    margin-bottom: ${SPACERS.tiny};
  }
  .button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${SPACERS.medium};
    margin-top: ${SPACERS.small};
    button {
      min-width: 100px;
    }
  }
  .investment-row-wrapper {
    overflow: auto;
    max-height: 60vh;
    ${thinScrollbarStyles}
    .investment-row {
      transition: all 0.5s;
      padding-top: ${SPACERS.medium};
      & > div {
        margin-bottom: ${SPACERS.extraTiny};
      }
    }
  }
  .copy-buttons-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-top: ${SPACERS.small};
    padding-bottom: ${SPACERS.extraTiny};
  }
`;

const CellUpdateModalContent: React.FC<CellUpdateModalContentProps> = ({
  value,
  row,
  columns,
  handleClose,
  handleUpdateRow,
}) => {
  const [showRow, setShowRow] = useState<boolean>(false);
  const [text, setText] = useState<string>(value.value);
  const [error, setError] = useState<boolean>(false);

  const copyHeaders = useCallback(
    (onCallback?: () => unknown) => {
      copyToClipboard(columns.columns.join(','), onCallback);
    },
    [columns.columns],
  );
  const copyRow = useCallback(
    (onCallback?: () => unknown) => {
      copyToClipboard(row.slice(1).join(','), onCallback);
    },
    [row],
  );

  const handleToggleRowValues = useCallback(() => {
    setShowRow(o => !o);
  }, [setShowRow]);

  const handleSave = () => {
    if (investmentUploadColumnValidations[value.systemKey](text, row)) {
      handleUpdateRow(
        value.rowIndex,
        value.columnIndex,
        value.systemKey,
        investmentPrettifyText(value.systemKey, text),
      );
      handleClose();
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <StyledDiv>
      <TrueImpactText variant="body-small" className="textfield-tag">
        <strong>{columns.columns[value.columnIndex]}: </strong>
        {isInvestmentColumnDate(value.systemKey)
          ? '(YYYY-MM-DD or MM-DD-YYYY)'
          : ''}
      </TrueImpactText>
      <TrueImpactTextfield
        value={text}
        autoFocus={true}
        onChange={setText}
        isError={error}
        onEnterPress={handleSave}
        placeholder={investmentUploadColumns[value.systemKey][0]}
      />
      <div className="button-wrapper">
        <TrueImpactButton
          variant="secondary-large"
          onClick={handleToggleRowValues}
        >
          {showRow ? (
            <>
              <FontAwesomeIcon icon={faEyeSlash} />
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faEye} />
            </>
          )}
        </TrueImpactButton>
        <TrueImpactButton onClick={handleSave}>Save</TrueImpactButton>
      </div>
      <TrueImpactCollapse
        show={showRow}
        className="investment-row"
        wrapperClassName="investment-row-wrapper"
      >
        {row.map((r, i) => (
          <TrueImpactText variant="body-small" key={`investment-row-${i}`}>
            {i === 0 ? 'Row Index' : columns.columns[i - 1]}:{' '}
            <strong>{r}</strong>
          </TrueImpactText>
        ))}
        <div className="copy-buttons-wrapper">
          <CopyButton text="Copy headers" onCopy={copyHeaders} />
          <CopyButton text="Copy this row" onCopy={copyRow} />
        </div>
      </TrueImpactCollapse>
    </StyledDiv>
  );
};

export default CellUpdateModalContent;
